import { useEffect } from 'react';
import { Loader2, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { AdminGuard } from '../components/guards';
import { QueueRow } from '../components/matching';
import { useMatchingStore } from '../store/matchingStore';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

export const MatchingReview = () => {
  const rows = useMatchingStore((s) => s.rows);
  const pagination = useMatchingStore((s) => s.pagination);
  const stats = useMatchingStore((s) => s.stats);
  const filters = useMatchingStore((s) => s.filters);
  const isLoading = useMatchingStore((s) => s.isLoading);
  const error = useMatchingStore((s) => s.error);
  const fetchQueue = useMatchingStore((s) => s.fetchQueue);
  const fetchStats = useMatchingStore((s) => s.fetchStats);
  const setPage = useMatchingStore((s) => s.setPage);
  const setFilters = useMatchingStore((s) => s.setFilters);

  useEffect(() => {
    void fetchStats();
    void fetchQueue(1);
  }, [fetchStats, fetchQueue]);

  return (
    <AdminGuard>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Dedup Review Queue
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {stats.possibleDuplicate.toLocaleString()} NCES candidates pending admin review
            </p>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={filters.state ?? ''}
              onChange={(e) => setFilters({ state: e.target.value || undefined })}
              className="text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-1.5"
            >
              <option value="">All states</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select
              value={filters.sortBy}
              onChange={(e) => setFilters({ sortBy: e.target.value as 'confidence' | 'similarity' })}
              className="text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-1.5"
            >
              <option value="confidence">Sort: confidence</option>
              <option value="similarity">Sort: similarity</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-4 mb-4 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        )}

        {isLoading && rows.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            Loading queue...
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-gray-500 dark:text-gray-400">
            Queue cleared.
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((row) => (
              <QueueRow key={row.id} row={row} />
            ))}
          </div>
        )}

        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 text-sm text-gray-600 dark:text-gray-400">
            <div>
              Page {pagination.page} of {pagination.totalPages} ({pagination.total.toLocaleString()} rows)
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(pagination.page - 1)}
                disabled={pagination.page <= 1 || isLoading}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </button>
              <button
                type="button"
                onClick={() => setPage(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages || isLoading}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminGuard>
  );
};
